/**
 * Positions Hooks (Phase 9.1)
 *
 * TanStack Query hooks for position CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  PositionWithCount,
  PositionLookup,
  Position,
  CreatePosition,
  UpdatePosition,
} from '@guild/shared';

// Query keys
export const positionKeys = {
  all: ['positions'] as const,
  lists: () => [...positionKeys.all, 'list'] as const,
  list: (filters: { search?: string; departmentId?: string; includeInactive?: boolean }) =>
    [...positionKeys.lists(), filters] as const,
  lookup: () => [...positionKeys.all, 'lookup'] as const,
  details: () => [...positionKeys.all, 'detail'] as const,
  detail: (id: string) => [...positionKeys.details(), id] as const,
};

/**
 * Fetch positions list with operator counts
 */
export function usePositions(options?: {
  search?: string;
  departmentId?: string;
  includeInactive?: boolean;
}) {
  const api = useApi();

  return useQuery({
    queryKey: positionKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.departmentId) params.set('departmentId', options.departmentId);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/positions?${queryString}` : '/api/positions';

      return api.get<PositionWithCount[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight positions list for dropdowns
 */
export function usePositionLookup() {
  const api = useApi();

  return useQuery({
    queryKey: positionKeys.lookup(),
    queryFn: () => api.get<PositionLookup[]>('/api/positions/lookup'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (rarely changes)
  });
}

/**
 * Fetch single position
 */
export function usePosition(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: positionKeys.detail(id),
    queryFn: () => api.get<Position>(`/api/positions/${id}`),
    enabled: !!id,
  });
}

/**
 * Create position mutation
 */
export function useCreatePosition() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePosition) => api.post<Position>('/api/positions', data),
    onSuccess: () => {
      // Invalidate all position queries
      queryClient.invalidateQueries({ queryKey: positionKeys.all });
    },
  });
}

/**
 * Update position mutation
 */
export function useUpdatePosition() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePosition }) =>
      api.put<Position>(`/api/positions/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: positionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: positionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: positionKeys.lookup() });
    },
  });
}

/**
 * Delete position mutation (soft delete)
 */
export function useDeletePosition() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/positions/${id}`),
    onSuccess: () => {
      // Invalidate all position queries
      queryClient.invalidateQueries({ queryKey: positionKeys.all });
    },
  });
}
